import React, { useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import Modal from 'react-modal';
import styles from './StepFrequencyChart.module.css';
import GoToDashboardButton from '../components/GoToDashboardButton';
import SourceFileSummary from './SourceFileSummary';
import { fetchAteSwVersions, fetchStepFrequency, fetchStepNumbers, fetchTestspecs } from './stepFrequency/stepFrequencyApi';
import {
    buildStepChartData,
    filterStepRows,
    getQuickRange,
    getStepMetrics,
    normalizeStepFrequencyRows,
    stepChartOptions,
} from './stepFrequency/stepFrequencyData';
import { exportStepTableToCSV, exportStepTableToPDF } from './stepFrequency/stepFrequencyExport';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const StepFrequencyChart = () => {
    const [rawStepData, setRawStepData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [ateSwVersion, setAteSwVersion] = useState('');
    const [ateSwVersions, setAteSwVersions] = useState([]);
    const [testspecs, setTestspecs] = useState([]);
    const [selectedTestspec, setSelectedTestspec] = useState('');
    const [stepNumbers, setStepNumbers] = useState([]);
    const [selectedStepNumbers, setSelectedStepNumbers] = useState([]);
    const [selectedFamilies, setSelectedFamilies] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const [showOnlyStepsWithValues, setShowOnlyStepsWithValues] = useState(true);

    const tableData = useMemo(
        () => filterStepRows(rawStepData, showOnlyStepsWithValues),
        [rawStepData, showOnlyStepsWithValues]
    );
    const chartData = useMemo(() => buildStepChartData(tableData), [tableData]);
    const metrics = useMemo(() => getStepMetrics(tableData), [tableData]);
    const hasData = tableData.length > 0;

    const loadMetadata = async () => {
        try {
            const [versions, steps, specs] = await Promise.all([
                fetchAteSwVersions(),
                fetchStepNumbers(),
                fetchTestspecs(),
            ]);
            setAteSwVersions(versions);
            setStepNumbers(steps);
            setTestspecs(specs);
        } catch (metadataError) {
            console.error('Error fetching step filter metadata:', metadataError);
            setError('Some filter options could not be loaded.');
        }
    };

    const loadData = async (filters = {}) => {
        setLoading(true);
        setError(null);

        try {
            const rows = await fetchStepFrequency({
                startDate,
                endDate,
                ateSwVersion,
                selectedTestspec,
                selectedStepNumbers,
                selectedFamilies,
                ...filters,
            });
            setRawStepData(normalizeStepFrequencyRows(rows));
        } catch (fetchError) {
            console.error('Error fetching step frequency data:', fetchError);
            setRawStepData([]);
            setError('Failed to load step frequency data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadMetadata();
        loadData({ startDate: null, endDate: null, ateSwVersion: '', selectedTestspec: '', selectedStepNumbers: [], selectedFamilies: [] });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyQuickRange = (range) => {
        const nextRange = getQuickRange(range);
        setStartDate(nextRange.start);
        setEndDate(nextRange.end);
    };

    const handleApplyFilters = () => {
        loadData();
    };

    const resetFilters = () => {
        setStartDate(null);
        setEndDate(null);
        setAteSwVersion('');
        setSelectedTestspec('');
        setSelectedStepNumbers([]);
        setSelectedFamilies([]);
        setShowOnlyStepsWithValues(true);
        loadData({ startDate: null, endDate: null, ateSwVersion: '', selectedTestspec: '', selectedStepNumbers: [], selectedFamilies: [] });
    };

    return (
        <div className={styles.pageContainer}>
            <GoToDashboardButton />

            <header className={styles.pageHeader}>
                <div>
                    <p className={styles.eyebrow}>Step quality</p>
                    <h2 className={styles.chartTitle}>Step Frequency and Fail Percentage</h2>
                    <p className={styles.description}>
                        Find the test steps with the highest fail rate and inspect the execution counts behind each bar.
                    </p>
                </div>
                <button className={styles.helpButtonInline} onClick={() => setIsHelpOpen(true)}>
                    Page help
                </button>
            </header>

            {error && <div className={styles.errorMessage}>{error}</div>}

            <section className={styles.filterSection}>
                <div className={styles.filterHeader}>
                    <div>
                        <h3>Filters</h3>
                        <p>Use test dates, software version, and step numbers to focus the analysis.</p>
                    </div>
                    <button onClick={resetFilters} className={styles.resetButton}>
                        Reset
                    </button>
                </div>

                <div className={styles.filterGrid}>
                    <div className={styles.datePickerGroup}>
                        <label>Start date</label>
                        <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            placeholderText="Any start date"
                            dateFormat="yyyy-MM-dd"
                            className={styles.datePicker}
                            isClearable
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                        />
                    </div>

                    <div className={styles.datePickerGroup}>
                        <label>End date</label>
                        <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            placeholderText="Any end date"
                            dateFormat="yyyy-MM-dd"
                            className={styles.datePicker}
                            isClearable
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            minDate={startDate}
                        />
                    </div>

                    <div className={styles.datePickerGroup}>
                        <label>ATE SW version</label>
                        <select
                            value={ateSwVersion}
                            onChange={(event) => setAteSwVersion(event.target.value)}
                            className={styles.dropdown}
                        >
                            <option value="">All versions</option>
                            {ateSwVersions.map((version) => (
                                <option key={version.AteSwVersion} value={version.AteSwVersion}>
                                    {version.AteSwVersion} ({version.count})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={`${styles.datePickerGroup} ${styles.stepPicker}`}>
                        <label>Step numbers</label>
                        <Select
                            isMulti
                            options={stepNumbers}
                            value={selectedStepNumbers}
                            onChange={(values) => setSelectedStepNumbers(values || [])}
                            className={styles.multiSelect}
                            classNamePrefix="chartSelect"
                            placeholder="All steps"
                        />
                    </div>

                    <div className={styles.datePickerGroup}>
                        <label>Testspec</label>
                        <select
                            value={selectedTestspec}
                            onChange={(event) => setSelectedTestspec(event.target.value)}
                            className={styles.dropdown}
                        >
                            <option value="">All testspecs</option>
                            {testspecs.map((spec) => (
                                <option key={spec.Testspec} value={spec.Testspec}>{spec.Testspec}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className={styles.quickRangeContainer}>
                    <button className={styles.quickRangeBtn} onClick={() => applyQuickRange('today')}>Today</button>
                    <button className={styles.quickRangeBtn} onClick={() => applyQuickRange('last7')}>Last 7 days</button>
                    <button className={styles.quickRangeBtn} onClick={() => applyQuickRange('month')}>This month</button>
                    <button className={styles.quickRangeBtn} onClick={() => applyQuickRange('last30')}>Last 30 days</button>
                    <button onClick={handleApplyFilters} className={styles.applyButton}>Apply filters</button>
                </div>

                <SourceFileSummary
                    compact
                    startDate={startDate}
                    endDate={endDate}
                    ateSwVersion={ateSwVersion}
                    testspec={selectedTestspec}
                    selectedFamilies={selectedFamilies}
                    onFamiliesChange={setSelectedFamilies}
                    title="Source files used for dashboard charts"
                />
            </section>

            <section className={styles.metricsGrid}>
                {metrics.map((metric) => (
                    <div className={`${styles.metricCard} ${styles[metric.tone]}`} key={metric.label}>
                        <span>{metric.label}</span>
                        <strong>{metric.value}</strong>
                    </div>
                ))}
            </section>

            <section className={styles.chartPanel}>
                <div className={styles.panelHeader}>
                    <div>
                        <h3>Fail percentage by step</h3>
                        <p>Bars are sorted by fail percentage, using the currently selected filters.</p>
                    </div>
                    <div className={styles.panelActions}>
                        <button
                            onClick={() => setShowOnlyStepsWithValues((prev) => !prev)}
                            className={styles.toggleButton}
                        >
                            {showOnlyStepsWithValues ? 'Show all steps' : 'Show only steps with values'}
                        </button>
                        <button onClick={() => setIsModalOpen(true)} className={styles.enlargeButton}>
                            Enlarge chart
                        </button>
                    </div>
                </div>

                <div className={styles.chartContainer}>
                    {loading ? (
                        <div className={styles.loadingScreen}>
                            <div className={styles.spinner}></div>
                            <p>Loading step data...</p>
                        </div>
                    ) : hasData ? (
                        <Bar data={chartData} options={stepChartOptions()} />
                    ) : (
                        <div className={styles.emptyState}>No step data found for these filters.</div>
                    )}
                </div>
            </section>

            <section className={styles.tableToggle}>
                <div className={styles.panelHeader}>
                    <div>
                        <h3>Step details</h3>
                        <p>Total executions, fail counts, fail rate, and average failed measurement by step.</p>
                    </div>
                    <div className={styles.exportButtonGroup}>
                        <button onClick={() => setShowDetails((visible) => !visible)} className={styles.toggleButton}>
                            {showDetails ? 'Hide details' : 'Show details'}
                        </button>
                        <button onClick={() => exportStepTableToPDF(tableData)} className={styles.exportButton} disabled={!hasData}>
                            PDF
                        </button>
                        <button onClick={() => exportStepTableToCSV(tableData)} className={styles.exportButton} disabled={!hasData}>
                            CSV
                        </button>
                    </div>
                </div>

                {showDetails && (
                    <div className={styles.tableWrap}>
                        <table className={styles.detailsTable}>
                            <thead>
                                <tr>
                                    <th>Step number</th>
                                    <th>Total frequency</th>
                                    <th>Fail count</th>
                                    <th>Fail percentage (%)</th>
                                    <th>Average fail measure value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tableData.map((item) => (
                                    <tr key={item.stepNumber}>
                                        <td>{`Step ${item.stepNumber}`}</td>
                                        <td>{item.totalFrequency}</td>
                                        <td>{item.failCount}</td>
                                        <td>{item.failPercentage.toFixed(2)}</td>
                                        <td>{item.avgMeasureValue !== null ? item.avgMeasureValue.toFixed(2) : '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {isModalOpen && (
                <div className={styles.modal}>
                    <div className={styles.modalContent}>
                        <button onClick={() => setIsModalOpen(false)} className={styles.closeModalButton}>
                            Close
                        </button>
                        <div className={styles.expandedChartContainer}>
                            <Bar data={chartData} options={stepChartOptions('Fail Percentage by Step Number (Expanded)')} />
                        </div>
                    </div>
                </div>
            )}

            <Modal
                isOpen={isHelpOpen}
                onRequestClose={() => setIsHelpOpen(false)}
                className={styles.helpModalContent}
                overlayClassName={styles.helpModalOverlay}
                contentLabel="Help Information"
            >
                <button onClick={() => setIsHelpOpen(false)} className={styles.closeHelpButton}>
                    Close
                </button>
                <div className={styles.helpText}>
                    <h2>About this page</h2>
                    <p>
                        This page ranks test steps by failure rate. The data comes from uploaded test records, grouped by step number and filtered by date, ATE software version, and selected steps.
                    </p>
                    <p>
                        The table gives the raw execution count, fail count, calculated fail percentage, and average measurement value for failed executions.
                    </p>
                </div>
            </Modal>
        </div>
    );
};

export default StepFrequencyChart;
